<script setup lang="ts">
import BaseImage from '@/components/images/BaseImage.vue';
import { mainNavigationQaEquipment } from '@/components/icons/icons';
import { computed } from 'vue';
import { RouteLocationRaw } from 'vue-router';
import { batteryDisplayOf } from './deviceStatusIslandState';
import { DeviceStatusIslandBattery } from './deviceStatusIslandTypes';

const props = defineProps<{
  pictureId?: string | null;
  serialNumber?: string | null;
  typeNumber?: string | null;
  orientation?: 'landscape' | 'portrait';
  battery?: DeviceStatusIslandBattery;
  showBattery?: boolean;
  width?: number;
  /** Detail-page target; when set the thumb renders as a router-link. */
  to?: RouteLocationRaw;
  /** Device name, used only for the link's accessible label. */
  name?: string;
}>();

const isPortrait = computed(() => props.orientation === 'portrait');
const batteryInfo = computed(() => (props.battery ? batteryDisplayOf(props.battery) : undefined));
const containerStyle = computed(() => (props.width ? { width: `${props.width}px` } : undefined));
</script>

<template>
  <component
    :is="to ? 'router-link' : 'div'"
    class="island-thumb"
    :class="{ 'island-thumb--portrait': isPortrait, 'island-thumb--link': !!to }"
    :style="containerStyle"
    :to="to"
    :aria-label="to && name ? $t('DeviceStatusIslandViewDetails', { value: name }) : undefined"
  >
    <div class="island-thumb__image">
      <BaseImage
        :pictureId="pictureId"
        :fallbackIcon="mainNavigationQaEquipment"
        :width="80"
        :aspectRatio="1"
        generateSrcset
        hideImageFrame
        hideLoadingPlaceholder
        cover
      />
    </div>

    <div v-if="showBattery || typeNumber || serialNumber" class="island-thumb__info text-caption">
      <span v-if="showBattery" class="island-thumb__battery">
        <template v-if="batteryInfo">
          <v-icon
            :color="batteryInfo.color"
            :icon="batteryInfo.icon"
            size="18"
            :class="{ 'island-thumb__battery-icon--sideways': batteryInfo.sideways }"
          />
          {{ batteryInfo.label ?? $t('PowerAdapter') }}
        </template>
        <template v-else>{{ $t('NoBatteryData') }}</template>
      </span>
      <span v-if="typeNumber">{{ $t('DeviceStatusIslandReference', { value: typeNumber }) }}</span>
      <span v-if="serialNumber">{{ $t('DeviceStatusIslandSerial', { value: serialNumber }) }}</span>
    </div>
  </component>
</template>

<style lang="scss" scoped>
.island-thumb {
  // Landscape (default): thumbnail and info side by side, info centred against
  // the thumbnail. Never grow/shrink; the caller may fix the width.
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
  flex: 0 0 auto;

  &--portrait {
    flex-direction: column;
    gap: 6px;
  }

  &--link {
    color: inherit;
    text-decoration: none;
    cursor: pointer;
    &:hover,
    &:focus-visible {
      opacity: 1;
    }

    &:hover .island-thumb__image,
    &:focus-visible .island-thumb__image {
      transform: scale(1.03);
    }
  }

  &__image {
    flex: 0 0 80px;
    width: 80px;
    height: 80px;
    overflow: hidden;
    border-radius: 10px;
    transition: transform 0.15s ease;

    :deep(.v-img) {
      width: 100%;
      height: 100%;
    }
  }

  &__info {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
    line-height: 1.25;
    color: rgba(var(--v-theme-on-surface), 0.7);
    white-space: nowrap;
  }

  &--portrait &__info {
    align-items: center;
    text-align: center;
  }

  &__battery {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    white-space: nowrap;

    color: rgba(var(--v-theme-on-surface), 0.9);
  }

  &__battery-icon--sideways {
    transform: rotate(90deg);
  }
}
</style>
